import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import { EditActions, editActions } from "../../actions/editActions";
import { nameofFactory, hasWriteAccess, isAdmin } from "../../utils/utils";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import DropdownField from "../../components/Editing/DropdownField";
import { getKceVersionOptions, getKceProductOptions } from "../../utils/pdApiUtils";
import CheckboxField from "../../components/Editing/CheckboxField";

type StateProps = {
  editedIoConfiguration: Api.IoConfiguration;
  storedIoConfiguration: Api.IoConfiguration;
  pdApi: State.PdApi;
  ioConfig: State.IoConfig;
  activeUser?: Api.User;
};

type DispatchProps = {
  editActions: typeof editActions;
};
type OwnProps = {
  toggleModal: () => void;
};

const ioConfigurationField = nameofFactory<Api.IoConfiguration>();

export type EditedIoConfigurationField = StateProps & DispatchProps & OwnProps;

export class EditIoConfigurations extends React.Component<EditedIoConfigurationField> {
  render(): JSX.Element {
    const {
      editedIoConfiguration,
      pdApi,
      storedIoConfiguration,
      activeUser,
      toggleModal
    } = this.props;
    const kceVersionOptions = getKceVersionOptions(pdApi.kceVersions);
    const kceProductOptions = getKceProductOptions(pdApi.kceProducts);
    const isNew = !storedIoConfiguration;

    return (
      <Editor
        itemName={"IO configuration"}
        originalObject={storedIoConfiguration}
        editedObject={editedIoConfiguration}
        hasWritePermission={hasWriteAccess(activeUser, "Lift")}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update("ioConfigurations" as Api.OptionsGrud)}
        create={() => this.props.editActions.add("ioConfigurations" as Api.OptionsGrud)}
        copy={
          isNew
            ? undefined
            : () =>
                this.props.editActions.startEditing(
                  {
                    ...editedIoConfiguration,
                    ioboards: this.props.ioConfig.ioBoards
                  } as Api.IoConfiguration,
                  true
                )
        }
        remove={
          isNew
            ? undefined
            : {
                callback: toggleModal,
                disabledTooltip: ".Kindly contact Admin to delete this IO Configuration.",
                disabled: isAdmin(activeUser) ? false : true
              }
        }
      >
        <CheckboxField
          id={"ioconfigLocked"}
          label={"Locked"}
          checked={editedIoConfiguration.locked}
          onChange={value =>
            this.props.editActions.setEdited({ ...editedIoConfiguration, locked: value })
          }
        />
        <TextField
          id="ioconfigFriendlyName"
          label="Friendly name"
          value={editedIoConfiguration.friendlyName}
          isError={!this.isFieldValid(ioConfigurationField("friendlyName"))}
          errorMessage={
            !this.isEmptyString(editedIoConfiguration.friendlyName)
              ? "The friendly name must be unique."
              : ""
          }
          onChange={friendlyName =>
            this.props.editActions.setEdited({ ...editedIoConfiguration, friendlyName })
          }
        />
        <DropdownField
          id="ioconfigKceVersion"
          label="Software version"
          value={
            editedIoConfiguration && editedIoConfiguration.kceVersion
              ? editedIoConfiguration.kceVersion.id
              : kceVersionOptions[0].value
          }
          options={kceVersionOptions}
          onChange={id => {
            const allKceVersions = _.values(this.props.pdApi.kceVersions);
            const kceVersion = _.find(allKceVersions, item => item.id === id);
            this.props.editActions.setEdited({
              ...editedIoConfiguration,
              kceVersion: kceVersion
            });
          }}
        />
        <DropdownField
          id="ioconfigKceProduct"
          label="Product"
          value={
            editedIoConfiguration && editedIoConfiguration.kceProduct
              ? editedIoConfiguration.kceProduct.id
              : kceProductOptions[0].value
          }
          options={kceProductOptions}
          onChange={id => {
            const allKceProducts = _.values(this.props.pdApi.kceProducts);
            const kceProduct = _.find(allKceProducts, item => item.id === id);
            this.props.editActions.setEdited({
              ...editedIoConfiguration,
              kceProduct: kceProduct
            });
          }}
        />
        <TextField
          id="ioconfigComment"
          label="Comment"
          value={editedIoConfiguration.comment}
          isError={!this.isFieldValid(ioConfigurationField("comment"))}
          onChange={comment =>
            this.props.editActions.setEdited({ ...editedIoConfiguration, comment })
          }
        />
      </Editor>
    );
  }

  private isEmptyString = (str: string): boolean => !(!!str.trim() && str.trim().length > 0);

  private isFieldValid = (field: string): boolean => {
    const { editedIoConfiguration, storedIoConfiguration, ioConfig } = this.props;
    const isNew = !storedIoConfiguration;
    switch (field) {
      case ioConfigurationField("friendlyName"): {
        const { friendlyName } = editedIoConfiguration;
        if (!friendlyName || friendlyName.trim().length === 0) {
          return false;
        } else {
          return _.values(ioConfig.ioConfigurations).find(
            ioConfiguration =>
              (isNew || ioConfiguration.id !== editedIoConfiguration.id) &&
              ioConfiguration.friendlyName.trim() === editedIoConfiguration.friendlyName.trim()
          )
            ? false
            : true;
        }
      }
      default:
        return true;
    }
  };

  private isAllFieldsValid = (): boolean => {
    const { editedIoConfiguration } = this.props;
    const temp = _.keys(editedIoConfiguration);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedIoConfiguration: state.editView.editedObject,
  storedIoConfiguration: state.editView.storedObject,
  pdApi: state.pdApi,
  ioConfig: state.ioConfig,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditIoConfigurations);


